import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { STRINGS } from "@/lib/i18n";
import { trackEvent } from "@/lib/analytics";
import { storage } from "@/lib/storage";

const LanguageContext = createContext(null);

const LANGS = Object.keys(STRINGS);

// `?lang=xx` in the URL wins over the saved preference (deep links, Play Console privacy URL).
const initialLang = () => {
  const fromUrl = new URLSearchParams(window.location.search).get("lang");
  return LANGS.includes(fromUrl) ? fromUrl : storage.get("atlas.lang", "ar");
};

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(initialLang);

  const setLang = useCallback((next) => {
    const prev = storage.get("atlas.lang", "ar");
    if (prev !== next) trackEvent("change_language", { from_lang: prev, to_lang: next });
    setLangState(next);
    storage.set("atlas.lang", next);
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute("lang", lang);
    html.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
  }, [lang]);

  const t = useCallback((key) => (STRINGS[lang] && STRINGS[lang][key]) || STRINGS.en[key] || key, [lang]);

  const value = { lang, setLang, t, isRtl: lang === "ar" };
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used inside LanguageProvider");
  return ctx;
}
